import { getLocalTimeZone, today } from '@internationalized/date';
import { maybe, mt, rd, type MutationData } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { WithFormatService } from 'cockpit/services/format/FormatService';
import {
  Check,
  CircleAlert,
  Copy,
  Info,
  Link2,
  Mail,
  Search,
  Trash2
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Resource } from '__types__';
import { Alert, AlertDescription } from 'v5/design-system/Alert';
import { Button } from 'v5/design-system/Button';
import {
  DateRangeInput,
  isCompleteDateRange
} from 'v5/design-system/DateRangeInput';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from 'v5/design-system/Form';
import { TextArea2 } from 'v5/design-system/Input2';
import {
  InputGroup2,
  InputGroup2Button,
  InputGroup2Input
} from 'v5/design-system/InputGroup2';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'v5/design-system/Select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from 'v5/design-system/Tabs';
import { DialogBody } from 'v5/design-system/Dialog';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { UserAvatar } from 'v5/patterns/widgets/user-avatar/UserAvatar';
import type { DateRange } from 'v5/platform/lang/dateRange';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';

import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { WithAvailabilityRequestService } from 'v5/services/io/AvailabilityRequestService/AvailabilityRequestService';
import { WithResourceMutationService } from 'v5/services/io/ResourceMutationService/ResourceMutationService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { RequestAvailabilityAddEmail } from './RequestAvailabilityAddEmail';
import { RequestAvailabilityEmailConfirm } from './RequestAvailabilityEmailConfirm';
import { RequestAvailabilityResourceSearch } from './RequestAvailabilityResourceSearch';

type Channel = 'email' | 'link';
type Step = 'compose' | 'recipients' | 'confirm';

/** Exclusive UI phases for footer/mutation/primary action (link result overrides step). */
type DialogView =
  | 'link-result'
  | 'confirm'
  | 'recipients'
  | 'compose-link'
  | 'compose-email';

type ComposeFormValues = {
  dateRange: DateRange | null;
  message: string;
};

export interface RequestAvailabilityDialogContentProps
  extends WithServices<
    [
      WithAvailabilityRequestService,
      WithResourceService,
      WithResourceMutationService,
      WithFormatService,
      WithToastService,
      WithAppScopeService,
      WithSystemSettingsService
    ]
  > {
  open: boolean;
  resourceIds: number[];
  onClose: () => void;
}

export function RequestAvailabilityDialogContent({
  open,
  resourceIds,
  onClose,
  services
}: RequestAvailabilityDialogContentProps) {
  const { t } = useTranslation('availability-request', {
    keyPrefix: 'RequestAvailabilityDialog'
  });
  const formatCalendarDate = services.formatService.useCalendarDate();

  const [channel, setChannel] = useState<Channel>('email');
  const [step, setStep] = useState<Step>('compose');
  const [linkResourceId, setLinkResourceId] = useState(resourceIds[0]);
  const [recipientIds, setRecipientIds] = useState(resourceIds);
  const [recipientEmails, setRecipientEmails] = useState<
    Record<number, string>
  >({});
  const [copied, setCopied] = useState(false);

  const defaultRange = useMemo((): DateRange => {
    const from = today(getLocalTimeZone());
    return { from, to: from.add({ days: 30 }) };
  }, []);

  const form = useForm<ComposeFormValues>({
    defaultValues: {
      dateRange: defaultRange,
      message: ''
    }
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setRecipientIds(resourceIds);
    setLinkResourceId(resourceIds[0]);
    setStep('compose');
    setChannel('email');
    setCopied(false);
  }, [open, resourceIds]);

  const resourcesRd = services.resourceService.useItemsById(recipientIds);
  const linkResourcesRd = services.resourceService.useItemsById(resourceIds);

  const recipientLists = useMemo(() => {
    const resources = rd.tryGet(resourcesRd);
    if (!resources) {
      return { missingEmail: [] as Resource[], withEmail: [] as Resource[] };
    }
    const byId = new Map(resources.map(resource => [resource.id, resource]));
    const ordered = recipientIds
      .map(id => byId.get(id))
      .filter((resource): resource is Resource => resource != null);
    return {
      missingEmail: ordered.filter(
        resource => resourceEmailAddresses(resource).length === 0
      ),
      withEmail: ordered.filter(
        resource => resourceEmailAddresses(resource).length > 0
      )
    };
  }, [resourcesRd, recipientIds]);

  const canSend =
    recipientLists.withEmail.length > 0 &&
    recipientLists.missingEmail.length === 0;

  useEffect(() => {
    const resources = rd.tryGet(resourcesRd);
    if (!resources) {
      return;
    }
    setRecipientEmails(prev => {
      const next = { ...prev };
      for (const resource of resources) {
        const emails = resourceEmailAddresses(resource);
        if (next[resource.id] == null || !emails.includes(next[resource.id])) {
          next[resource.id] = emails[0] ?? '';
        }
      }
      return next;
    });
  }, [resourcesRd]);

  const browsingScope = services.appScopeService.useResolvedScopeIdentity();

  const linkMutation = promiseState.useMutation(
    async (values: ComposeFormValues) => {
      if (!values.dateRange) {
        throw new Error(t('dateRangeRequired'));
      }
      if (linkResourceId == null) {
        throw new Error(t('resourceRequired'));
      }
      const result = await services.availabilityRequestService.createLink({
        resourceId: linkResourceId,
        dateRange: values.dateRange,
        scope: maybe.getOrThrow(browsingScope, 'Browsing scope is not resolved')
      });
      form.reset(values);
      return result;
    }
  );

  const sendMutation = promiseState.useMutation(async (_: void) => {
    const values = form.getValues();
    if (!values.dateRange) {
      throw new Error(t('dateRangeRequired'));
    }
    if (recipientIds.length === 0) {
      throw new Error(t('resourceRequired'));
    }
    const missingEmail = recipientIds.some(id => !recipientEmails[id]?.trim());
    if (missingEmail) {
      throw new Error(t('emailRequired'));
    }
    for (const resourceId of recipientIds) {
      await services.availabilityRequestService.createAndSendEmail({
        resourceId,
        resourceEmail: recipientEmails[resourceId],
        dateRange: values.dateRange,
        message: values.message || undefined,
        scope: maybe.getOrThrow(browsingScope, 'Browsing scope is not resolved')
      });
    }
    services.toastService.open({
      type: 'success',
      message: t('sendSuccessTitle', { count: recipientIds.length })
    });
    form.reset(values);
    onClose();
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    linkMutation.reset();
    sendMutation.reset();
  }, [open, resourceIds]);

  const isLinkResult = mt.isSuccess(linkMutation.state);
  const generatedUrl =
    mt.getResponse(linkMutation.state)?.resourcePortalUrl ?? '';
  const expiresOn = mt.getRequest(linkMutation.state)?.dateRange?.to ?? null;
  const view = getDialogView(isLinkResult, step, channel);

  useRegisterDirtyState(form.formState.isDirty && !isLinkResult);

  const activeMutation = getActiveMutation(
    view,
    linkMutation.state,
    sendMutation.state
  );

  const handleBackToCompose = () => {
    linkMutation.reset();
    sendMutation.reset();
    setStep('compose');
  };

  const handleBack = () => {
    if (step === 'confirm') {
      sendMutation.reset();
      setStep('recipients');
      return;
    }
    if (isLinkResult || step === 'recipients') {
      handleBackToCompose();
    }
  };

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [generatedUrl]);

  const saveText = getSaveText(view, t);

  const onPrimaryAction = () => {
    switch (view) {
      case 'link-result':
        onClose();
        return;
      case 'confirm':
        sendMutation.track(undefined);
        return;
      case 'recipients':
        if (!canSend) {
          return;
        }
        setStep('confirm');
        return;
      case 'compose-link':
        void form.handleSubmit(values => linkMutation.track(values))();
        return;
      case 'compose-email':
        void form.handleSubmit(values => {
          if (!values.dateRange) {
            form.setError('dateRange', { message: t('dateRangeRequired') });
            return;
          }
          setStep('recipients');
        })();
        return;
    }
  };

  const CopyIcon = copied ? Check : Copy;

  return (
    <Form {...form}>
      <FormDialogContent
        open={open}
        title={t('title')}
        mutation={activeMutation}
        className="max-w-180"
        saveText={saveText}
        onSubmit={onPrimaryAction}
        footerContent={
          <>
            {(step === 'recipients' || step === 'confirm' || isLinkResult) && (
              <Button type="button" variant="ghost" onClick={handleBack}>
                {t('back')}
              </Button>
            )}
            <DialogErrorFooter mutation={activeMutation} compact />
          </>
        }
        saveButtonSlot={
          <AsyncButton
            type="submit"
            mutation={activeMutation}
            variant="primary"
            disabled={step === 'recipients' && !canSend}
          >
            {saveText}
          </AsyncButton>
        }
        renderDialogBody={children => (
          <DialogBody className="min-h-50 space-y-6">{children}</DialogBody>
        )}
      >
        {isLinkResult ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <p className="text-base font-semibold text-fg-default">
                {t('generatedUrl')}
              </p>
              <InputGroup2
                size="md"
                variant="default"
                rightSlot={
                  <InputGroup2Button
                    type="button"
                    aria-label={copied ? t('copied') : t('copyUrl')}
                    onClick={() => {
                      void handleCopy();
                    }}
                  >
                    <CopyIcon className="size-4" />
                  </InputGroup2Button>
                }
              >
                <InputGroup2Input
                  readOnly
                  value={generatedUrl}
                  className="truncate"
                />
              </InputGroup2>
            </div>
            {expiresOn && (
              <p className="flex items-start gap-1.5 text-base text-fg-muted">
                <Info className="mt-0.5 size-4 shrink-0" />
                <span>
                  {t('urlExpiresOn', {
                    date: formatCalendarDate.full(expiresOn),
                    // Date is app-formatted (not user HTML); keep `/` unescaped.
                    interpolation: { escapeValue: false }
                  })}
                </span>
              </p>
            )}
          </div>
        ) : step === 'confirm' ? (
          (() => {
            const values = form.getValues();
            const { withEmail } = recipientLists;
            if (!values.dateRange || withEmail.length === 0) {
              return null;
            }
            return (
              <RequestAvailabilityEmailConfirm
                startDate={values.dateRange.from}
                endDate={values.dateRange.to}
                message={values.message}
                recipients={withEmail}
                services={services}
              />
            );
          })()
        ) : step === 'recipients' ? (
          <div className="space-y-4">
            <RequestAvailabilityResourceSearch
              services={services}
              onResourceSelect={resourceId =>
                setRecipientIds(prev =>
                  prev.includes(resourceId) ? prev : [...prev, resourceId]
                )
              }
            >
              <Button type="button" variant="secondary" className="w-full">
                <Search className="size-4" />
                {t('searchPlaceholder')}
              </Button>
            </RequestAvailabilityResourceSearch>
            {rd
              .journey(resourcesRd)
              .wait(
                <div className="h-20 animate-pulse rounded-md bg-bg-subtle" />
              )
              .catch(() => (
                <p className="text-base text-fg-danger">
                  {t('resourceRequired')}
                </p>
              ))
              .map(() => {
                const { missingEmail, withEmail } = recipientLists;
                if (missingEmail.length === 0 && withEmail.length === 0) {
                  return (
                    <p className="text-base text-fg-muted">
                      {t('noRecipients')}
                    </p>
                  );
                }
                return (
                  <div className="space-y-4">
                    {missingEmail.length > 0 && (
                      <div className="space-y-3">
                        <Alert variant="destructive" layout="horizontal">
                          <CircleAlert />
                          <AlertDescription>
                            {t('missingEmailsMessage')}
                          </AlertDescription>
                        </Alert>
                        <ul className="space-y-3">
                          {missingEmail.map(resource => (
                            <RecipientRow
                              key={resource.id}
                              resource={resource}
                              emailSlot={
                                <RequestAvailabilityAddEmail
                                  services={services}
                                  resource={resource}
                                  onSaved={savedEmail =>
                                    setRecipientEmails(prev => ({
                                      ...prev,
                                      [resource.id]: savedEmail
                                    }))
                                  }
                                />
                              }
                              onRemove={() => {
                                setRecipientIds(prev =>
                                  prev.filter(id => id !== resource.id)
                                );
                                setRecipientEmails(prev => {
                                  const next = { ...prev };
                                  delete next[resource.id];
                                  return next;
                                });
                              }}
                            />
                          ))}
                        </ul>
                      </div>
                    )}
                    {withEmail.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-base font-semibold text-fg-default">
                          {t('recipientsCount', { count: withEmail.length })}
                        </p>
                        <ul className="space-y-3">
                          {withEmail.map(resource => {
                            const emails = resourceEmailAddresses(resource);
                            const selectedEmail =
                              recipientEmails[resource.id] ?? emails[0] ?? '';
                            return (
                              <RecipientRow
                                key={resource.id}
                                resource={resource}
                                emailSlot={
                                  emails.length === 1 ? (
                                    <span className="truncate text-base text-fg-default">
                                      {emails[0]}
                                    </span>
                                  ) : (
                                    <Select
                                      value={selectedEmail}
                                      onValueChange={value =>
                                        setRecipientEmails(prev => ({
                                          ...prev,
                                          [resource.id]: value
                                        }))
                                      }
                                    >
                                      <SelectTrigger
                                        size="md"
                                        variant="regular"
                                      >
                                        <SelectValue placeholder={t('email')} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {emails.map(email => (
                                          <SelectItem key={email} value={email}>
                                            {email}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )
                                }
                                onRemove={() => {
                                  setRecipientIds(prev =>
                                    prev.filter(id => id !== resource.id)
                                  );
                                  setRecipientEmails(prev => {
                                    const next = { ...prev };
                                    delete next[resource.id];
                                    return next;
                                  });
                                }}
                              />
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="space-y-4">
            <Tabs
              value={channel}
              onValueChange={value => setChannel(value as Channel)}
            >
              <TabsList variant="default" className="w-fit">
                <TabsTrigger value="email">
                  <Mail className="size-4" />
                  {t('viaEmail')}
                </TabsTrigger>
                <TabsTrigger value="link">
                  <Link2 className="size-4" />
                  {t('viaLink')}
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value="email"
                tabIndex={-1}
                className="mt-4 space-y-4"
              >
                <ComposeDateField form={form} />
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)] items-start gap-x-4 gap-y-1 border-t border-border-default pt-4">
                      <div>
                        <p className="text-base font-semibold text-fg-default">
                          {t('addMessage')}
                        </p>
                        <p className="text-base text-fg-muted">
                          {t('addMessageHelper')}
                        </p>
                      </div>
                      <FormControl>
                        <TextArea2
                          {...field}
                          placeholder={t('messagePlaceholder')}
                          rows={4}
                          variant="default"
                          size="md"
                          className="h-auto min-h-0"
                        />
                      </FormControl>
                      <FormMessage className="col-span-2" />
                    </FormItem>
                  )}
                />
              </TabsContent>
              <TabsContent
                value="link"
                tabIndex={-1}
                className="mt-4 space-y-4"
              >
                <ComposeDateField form={form} />
                {resourceIds.length > 1 &&
                  (() => {
                    const resources = rd.tryGet(linkResourcesRd);
                    if (!resources) {
                      return null;
                    }
                    return (
                      <div className="space-y-2 border-t border-border-default pt-4">
                        <p className="text-base font-semibold text-fg-default">
                          {t('recipients')}
                        </p>
                        <Select
                          value={String(linkResourceId)}
                          onValueChange={value =>
                            setLinkResourceId(Number(value))
                          }
                        >
                          <SelectTrigger size="md" variant="regular">
                            <SelectValue placeholder={t('recipients')} />
                          </SelectTrigger>
                          <SelectContent>
                            {resources.map(resource => (
                              <SelectItem
                                key={resource.id}
                                value={String(resource.id)}
                              >
                                {resource.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })()}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </FormDialogContent>
    </Form>
  );
}

function RecipientRow({
  resource,
  emailSlot,
  onRemove
}: {
  resource: Resource;
  emailSlot: ReactNode;
  onRemove: () => void;
}) {
  const { t } = useTranslation('availability-request', {
    keyPrefix: 'RequestAvailabilityDialog'
  });
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] items-center gap-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <UserAvatar
          user={rd.of({
            avatarUrl: resource.profile_picture,
            name: resource.full_name
          })}
          size="sm"
        />
        <span className="truncate text-base font-medium">
          {resource.full_name}
        </span>
      </div>
      {emailSlot}
      <Button
        type="button"
        variant="ghost"
        layout="icon"
        size="sm"
        aria-label={t('removeRecipient')}
        onClick={onRemove}
      >
        <Trash2 className="size-4" />
      </Button>
    </li>
  );
}

function getDialogView(
  isLinkResult: boolean,
  step: Step,
  channel: Channel
): DialogView {
  if (isLinkResult) {
    return 'link-result';
  }
  switch (step) {
    case 'confirm':
      return 'confirm';
    case 'recipients':
      return 'recipients';
    case 'compose':
      return channel === 'link' ? 'compose-link' : 'compose-email';
  }
}

function getActiveMutation(
  view: DialogView,
  linkMutation: MutationData<ComposeFormValues, unknown>,
  sendMutation: MutationData<void, unknown>
): MutationData<unknown, unknown> {
  switch (view) {
    case 'confirm':
      return sendMutation;
    case 'compose-link':
      return linkMutation;
    case 'link-result':
    case 'recipients':
    case 'compose-email':
      return mt.ofIdle();
  }
}

function getSaveText(
  view: DialogView,
  t: (key: 'done' | 'confirmAndSend' | 'next' | 'generateLink') => string
): string {
  switch (view) {
    case 'link-result':
      return t('done');
    case 'confirm':
      return t('confirmAndSend');
    case 'compose-link':
      return t('generateLink');
    case 'recipients':
    case 'compose-email':
      return t('next');
  }
}

function resourceEmailAddresses(resource: Resource): string[] {
  return (
    resource.emails
      ?.map(email => email.value_1?.trim() ?? '')
      .filter(Boolean) ?? []
  );
}

function ComposeDateField({
  form
}: {
  form: ReturnType<typeof useForm<ComposeFormValues>>;
}) {
  const { t } = useTranslation('availability-request', {
    keyPrefix: 'RequestAvailabilityDialog'
  });
  return (
    <FormField
      control={form.control}
      name="dateRange"
      rules={{ required: t('dateRangeRequired') }}
      render={({ field }) => (
        <FormItem className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)] items-start gap-x-4 gap-y-1">
          <div>
            <p className="text-base font-semibold text-fg-default">
              {t('requestedDates')}
            </p>
            <p className="text-base text-fg-muted">
              {t('requestedDatesHelper')}
            </p>
          </div>
          <FormControl>
            <DateRangeInput
              value={field.value}
              onChange={range => {
                if (isCompleteDateRange(range)) {
                  field.onChange(range);
                }
              }}
              variant="regular"
              size="md"
              minValue={today(getLocalTimeZone())}
            />
          </FormControl>
          <FormMessage className="col-span-2" />
        </FormItem>
      )}
    />
  );
}
